// =====================================================
// CLINICAL RECORDS API SERVICE
// Servicio para comunicación con el backend de historias clínicas
// =====================================================

import {
  ClinicalTemplate,
  ClinicalRecord,
  RecordAttachment,
  RecordVersion,
  PatientSearchResult,
  TemplatesResponse,
  TemplateDetailResponse,
  RecordsResponse,
  RecordDetailResponse,
  PatientSearchResponse,
  PatientHistoryResponse,
  CreateRecordRequest,
  UpdateRecordRequest
} from '../types/clinicalRecords.types';
// --- Interfaces para el Template Builder ---

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  specialty?: string;
  icon?: string;
  color?: string;
  sections: {
    name: string;
    description?: string;
    is_collapsible?: boolean;
    is_expanded_default?: boolean;
    fields: {
      field_label: string;
      field_type: string;
      field_key?: string;
      placeholder?: string;
      default_value?: string;
      options?: string[];
      validation_rules?: any;
      is_required?: boolean;
      is_visible?: boolean;
      help_text?: string;
      width?: 'full' | 'half' | 'third';
    }[];
  }[];
}

export interface UpdateTemplateRequest extends CreateTemplateRequest {}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// =====================================================
// HELPERS
// =====================================================

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // Manejar errores de autenticación
    if (response.status === 401) {
      const errorMessage = (errorData.error || '').toLowerCase();
      if (errorMessage.includes('expirado') || errorMessage.includes('expired')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login?session_expired=true';
        throw new Error('Sesión expirada');
      }
    }
    
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
  }
  
  return response.json();
};

// =====================================================
// PLANTILLAS (TEMPLATES)
// =====================================================

/**
 * Obtener todas las plantillas disponibles para el profesional
 */
export const getTemplates = async (includeSystem: boolean = true): Promise<TemplatesResponse> => {
  const params = new URLSearchParams();
  if (!includeSystem) params.append('includeSystem', 'false');
  
  const queryString = params.toString();
  const url = `${API_BASE_URL}/api/clinical-records/templates${queryString ? `?${queryString}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse<TemplatesResponse>(response);
};

/**
 * Obtener una plantilla completa con sus secciones y campos
 */
export const getTemplateById = async (templateId: number): Promise<TemplateDetailResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/templates/${templateId}`,
    {
      method: 'GET',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse<TemplateDetailResponse>(response);
};

/**
 * Copiar una plantilla del sistema para personalizarla
 */
export const copySystemTemplate = async (
  templateId: number,
  newName?: string
): Promise<{ success: boolean; message: string; newTemplateId: number }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/templates/${templateId}/copy`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name: newName })
    }
  );
  
  return handleResponse<{ success: boolean; message: string; newTemplateId: number }>(response);
};

// =====================================================
// REGISTROS CLÍNICOS (RECORDS)
// =====================================================

/**
 * Obtener registros clínicos del profesional
 */
export const getRecords = async (params?: {
  status?: string;
  templateId?: number;
  patientName?: string;
  patientDni?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  filter?: string;
}): Promise<RecordsResponse> => {
  const searchParams = new URLSearchParams();
  
  if (params?.status) searchParams.append('status', params.status);
  if (params?.templateId) searchParams.append('templateId', params.templateId.toString());
  if (params?.patientName) searchParams.append('patientName', params.patientName);
  if (params?.patientDni) searchParams.append('patientDni', params.patientDni);
  if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom);
  if (params?.dateTo) searchParams.append('dateTo', params.dateTo);
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.offset) searchParams.append('offset', params.offset.toString());
  if (params?.filter) searchParams.append('filter', params.filter);
  
  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/clinical-records${queryString ? `?${queryString}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse<RecordsResponse>(response);
};

/**
 * Obtener un registro clínico por ID
 */
export const getRecordById = async (recordId: number): Promise<RecordDetailResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}`,
    {
      method: 'GET',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse<RecordDetailResponse>(response);
};

/**
 * Crear un nuevo registro clínico
 */
export const createRecord = async (data: CreateRecordRequest): Promise<{
  success: boolean;
  record: ClinicalRecord;
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }
  );
  
  return handleResponse(response);
};

/**
 * Actualizar un registro clínico existente
 */
export const updateRecord = async (
  recordId: number,
  data: UpdateRecordRequest
): Promise<{
  success: boolean;
  record: ClinicalRecord;
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }
  );
  
  return handleResponse(response);
};

/**
 * Eliminar un registro clínico (soft delete)
 */
export const deleteRecord = async (recordId: number): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse(response);
};

/**
 * Obtener versiones de un registro
 */
export const getRecordVersions = async (recordId: number): Promise<{
  success: boolean;
  versions: RecordVersion[];
}> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}/versions`,
    {
      method: 'GET',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse(response);
};

// =====================================================
// ADJUNTOS (ATTACHMENTS)
// =====================================================

/**
 * Subir un archivo adjunto a un registro
 */
export const uploadAttachment = async (
  recordId: number,
  file: File,
  description?: string,
  category?: string
): Promise<{
  success: boolean;
  attachment: RecordAttachment;
  message: string;
}> => {
  const token = localStorage.getItem('token');
  
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  if (category) formData.append('category', category);
  
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}/attachments`,
    {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
        // No incluir Content-Type para FormData
      },
      body: formData
    }
  );
  
  return handleResponse(response);
};

/**
 * Eliminar un archivo adjunto
 */
export const deleteAttachment = async (
  recordId: number,
  attachmentId: number
): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}/attachments/${attachmentId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse(response);
};

// =====================================================
// PACIENTES
// =====================================================

/**
 * Buscar pacientes del profesional
 */
export const searchPatients = async (
  query: string,
  limit: number = 10
): Promise<PatientSearchResponse> => {
  const params = new URLSearchParams({
    query,
    limit: limit.toString()
  });
  
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/patients/search?${params}`,
    {
      method: 'GET',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse<PatientSearchResponse>(response);
};

/**
 * Obtener historial clínico de un paciente
 */
export const getPatientHistory = async (params: {
  patientId?: number;
  patientDni?: string;
  patientEmail?: string;
}): Promise<PatientHistoryResponse> => {
  const searchParams = new URLSearchParams();
  
  if (params.patientId) searchParams.append('patientId', params.patientId.toString());
  if (params.patientDni) searchParams.append('patientDni', params.patientDni);
  if (params.patientEmail) searchParams.append('patientEmail', params.patientEmail);
  
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/patients/history?${searchParams}`,
    {
      method: 'GET',
      headers: getAuthHeaders()
    }
  );
  
  return handleResponse<PatientHistoryResponse>(response);
};

// =====================================================
// COMPARTIDOS (SHARES)
// =====================================================

/**
 * Buscar profesionales para compartir
 */
export const searchProfessionalsToShare = async (
  query: string
): Promise<{
  success: boolean;
  professionals: Array<{
    id: number;
    nombre: string;
    apellido: string;
    email: string;
    especialidad: string;
    picture: string | null;
  }>;
}> => {
  const params = new URLSearchParams({ query });
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/professionals/search?${params}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Compartir una historia clínica con otro profesional
 */
export const shareRecord = async (
  recordId: number,
  sharedWithProfessionalId: number,
  permission: string = 'full',
  notes?: string
): Promise<{ success: boolean; message: string; share: any }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}/share`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sharedWithProfessionalId, permission, notes })
    }
  );
  return handleResponse(response);
};

/**
 * Obtener con quién se compartió un registro
 */
export const getRecordShares = async (
  recordId: number
): Promise<{ success: boolean; shares: any[] }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}/shares`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

/**
 * Revocar acceso compartido
 */
export const revokeRecordShare = async (
  recordId: number,
  shareId: number
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/${recordId}/shares/${shareId}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  return handleResponse(response);
};

// =====================================================
// UTILIDADES
// =====================================================

/**
 * Validar los datos del formulario contra la plantilla
 */
export const validateFormData = (
  formData: Record<string, any>,
  template: ClinicalTemplate
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  
  if (!template.sections) {
    return { isValid: true, errors };
  }
  
  for (const section of template.sections) {
    for (const field of section.fields) {
      const value = formData[field.field_key];
      
      // Verificar campos requeridos
      if (field.is_required) {
        let isEmpty = (value === undefined || value === '' || value === null);
        
        // Para textarea_with_attachments, verificar que tenga texto o archivos
        if (field.field_type === 'textarea_with_attachments' && typeof value === 'object' && value !== null) {
          isEmpty = !value.text?.trim() && (!value.files || value.files.length === 0);
        }
        
        // Para drawing, verificar que tenga contenido
        if (field.field_type === 'drawing' && typeof value === 'string') {
          isEmpty = !value || !value.startsWith('data:image');
        }
        
        if (isEmpty) {
          errors[field.field_key] = `${field.field_label} es requerido`;
          continue;
        }
      }
      
      // Validaciones específicas por tipo
      if (value && field.validation_rules) {
        const rules = field.validation_rules;
        
        if (field.field_type === 'number') {
          const numValue = Number(value);
          if (rules.min !== undefined && numValue < rules.min) {
            errors[field.field_key] = `El valor mínimo es ${rules.min}`;
          }
          if (rules.max !== undefined && numValue > rules.max) {
            errors[field.field_key] = `El valor máximo es ${rules.max}`;
          }
        }
        
        if (field.field_type === 'text' || field.field_type === 'textarea') {
          const strValue = String(value);
          if (rules.minLength !== undefined && strValue.length < rules.minLength) {
            errors[field.field_key] = `Mínimo ${rules.minLength} caracteres`;
          }
          if (rules.maxLength !== undefined && strValue.length > rules.maxLength) {
            errors[field.field_key] = `Máximo ${rules.maxLength} caracteres`;
          }
          if (rules.pattern) {
            const regex = new RegExp(rules.pattern);
            if (!regex.test(strValue)) {
              errors[field.field_key] = rules.patternMessage || 'Formato inválido';
            }
          }
        }
      }
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
// --- Funciones API ---

/**
 * Crear una nueva plantilla personalizada
 */
export const createTemplate = async (
  data: CreateTemplateRequest
): Promise<{ success: boolean; template: ClinicalTemplate; message: string }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/templates`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }
  );
  return handleResponse(response);
};

/**
 * Actualizar una plantilla personalizada existente
 */
export const updateTemplate = async (
  templateId: number,
  data: UpdateTemplateRequest
): Promise<{ success: boolean; template: ClinicalTemplate; message: string }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/templates/${templateId}`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }
  );
  return handleResponse(response);
};

/**
 * Eliminar una plantilla personalizada
 */
export const deleteTemplate = async (
  templateId: number
): Promise<{ success: boolean; message: string; softDeleted: boolean }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/clinical-records/templates/${templateId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders()
    }
  );
  return handleResponse(response);
};
/**
 * Formatear fecha para mostrar
 */
export const formatRecordDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Obtener color de badge según antigüedad del registro
 */
export const getRecordAgeBadge = (createdAt: string): {
  label: string;
  color: string;
} => {
  const date = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return { label: 'Hoy', color: 'bg-green-100 text-green-700' };
  if (diffDays === 1) return { label: 'Ayer', color: 'bg-blue-100 text-blue-700' };
  if (diffDays <= 7) return { label: 'Esta semana', color: 'bg-indigo-100 text-indigo-700' };
  if (diffDays <= 30) return { label: 'Este mes', color: 'bg-purple-100 text-purple-700' };
  if (diffDays <= 90) return { label: 'Últimos 3 meses', color: 'bg-gray-100 text-gray-700' };
  return { label: 'Histórico', color: 'bg-gray-50 text-gray-500' };
};

// Exportar todo como servicio
const clinicalRecordsApi = {
  // Templates
  getTemplates,
  getTemplateById,
  copySystemTemplate,
  // Records
  getRecords,
  getRecordById,
  createRecord,
  updateRecord,
  deleteRecord,
  getRecordVersions,
  // Attachments
  uploadAttachment,
  deleteAttachment,
  // Patients
  searchPatients,
  getPatientHistory,
  // Shares
  searchProfessionalsToShare,
  shareRecord,
  getRecordShares,
  revokeRecordShare,
  // Utils
  validateFormData,
  formatRecordDate,
  createTemplate,      // ← nueva
updateTemplate,      // ← nueva
deleteTemplate,      // ← nueva
  getRecordAgeBadge
};

export default clinicalRecordsApi;